import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Box, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { HelmetProvider } from "react-helmet-async";
import PublicPageSeo from "./components/PublicPageSeo";
import AppShellSeo from "./components/AppShellSeo";

import TopBar from "./components/TopBar";
import { UserProvider, useUser } from "./contexts/UserContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import ModuleIntegrationsPage from "./pages/ModuleIntegrationsPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import axios from "axios";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const QuotationVerifyPage = lazy(() => import("./pages/QuotationVerifyPage"));

function RouteFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "40vh",
      }}
    >
      <CircularProgress color="secondary" />
    </Box>
  );
}

// Configure axios once for the whole app to use the backend base URL and send cookies.
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.withCredentials = true;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.data?.code === "ACCOUNT_DEACTIVATED") {
      try {
        localStorage.removeItem("user");
      } catch {
        // ignore
      }
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  },
);

/** Scrollable column for marketing/auth pages; dashboard uses its own main-pane scroll. */
function PublicScrollLayout() {
  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Outlet />
    </Box>
  );
}

const AppRoutes = () => {
  const user = useUser();
  const isAuthenticated = Boolean(user);

  return (
    <>
      <PublicPageSeo />
      <AppShellSeo />
      <TopBar />
      <Box
        sx={{
          mt: "64px",
          height: "calc(100vh - 64px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route
              path="/system-admin/*"
              element={
                !isAuthenticated ? (
                  <Navigate to="/login" replace />
                ) : user?.isPlatformAdmin ? (
                  <Navigate to="/dashboard/platform-admin/analytics" replace />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/dashboard/*"
              element={
                isAuthenticated ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Dashboard />
                  </Box>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route element={<PublicScrollLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route
                path="/integrations"
                element={<ModuleIntegrationsPage />}
              />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route
                path="/quotations/verify/:publicUuid"
                element={<QuotationVerifyPage />}
              />
            </Route>
          </Routes>
        </Suspense>
      </Box>
    </>
  );
};

function App() {
  const [theme, colorMode] = useMode();
  // Load initial user from localStorage; reactive auth state comes from UserContext.
  const initialUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HelmetProvider>
          <SnackbarProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <UserProvider user={initialUser}>
                <WebSocketProvider>
                  <AppRoutes />
                </WebSocketProvider>
              </UserProvider>
            </Router>
          </SnackbarProvider>
        </HelmetProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
